import Link from '@tiptap/extension-link'
import { mergeAttributes, type JSONContent } from '@tiptap/core'
import { Plugin, PluginKey, type EditorState } from '@tiptap/pm/state'

export const LINK_DEFAULT_COLOR = '#2563eb'
export const LINK_INHERIT_COLOR = 'inherit'

export const STYLED_LINK_ATTRIBUTES = { color: LINK_INHERIT_COLOR, underline: false }

export const linkStyle = (color: string | null | undefined, underline: boolean | null | undefined): string =>
  `color: ${color || LINK_DEFAULT_COLOR}; text-decoration: ${underline === false ? 'none' : 'underline'};`

export const EmailLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      color: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-color'),
        renderHTML: (attributes) => (attributes.color ? { 'data-color': attributes.color } : {})
      },
      underline: {
        default: true,
        parseHTML: (element) => element.getAttribute('data-underline') !== 'false',
        renderHTML: (attributes) => (attributes.underline === false ? { 'data-underline': 'false' } : {})
      }
    }
  },

  renderHTML({ mark, HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        style: linkStyle(mark.attrs.color, mark.attrs.underline)
      }),
      0
    ]
  },

  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() ?? []),
      new Plugin({
        key: new PluginKey('emailLinkStyle'),
        appendTransaction: (transactions, _oldState, state) =>
          transactions.some((transaction) => transaction.docChanged) ? styleNewLinks(state) : null
      })
    ]
  }
})

const styleNewLinks = (state: EditorState) => {
  const { link, underline, textStyle } = state.schema.marks
  const tr = state.tr

  state.doc.descendants((node, pos) => {
    const linkMark = node.isText ? node.marks.find((mark) => mark.type === link) : undefined
    if (!linkMark || linkMark.attrs.color) return

    const to = pos + node.nodeSize
    const style = node.marks.find((mark) => mark.type === textStyle)
    tr.addMark(pos, to, link.create({ ...linkMark.attrs, ...STYLED_LINK_ATTRIBUTES }))
    if (!node.marks.some((mark) => mark.type === underline)) tr.addMark(pos, to, underline.create())
    if (!style?.attrs.color) tr.addMark(pos, to, textStyle.create({ ...style?.attrs, color: LINK_DEFAULT_COLOR }))
  })

  return tr.docChanged ? tr : null
}

type Mark = NonNullable<JSONContent['marks']>[number]

const withLegacyLinkStyle = (marks: Mark[]): Mark[] => {
  const link = marks.find((mark) => mark.type === 'link')
  if (!link || link.attrs?.color) return marks

  const others = marks.filter((mark) => mark !== link && mark.type !== 'textStyle')
  const textStyle = marks.find((mark) => mark.type === 'textStyle')
  const underline = others.some((mark) => mark.type === 'underline') ? [] : [{ type: 'underline' }]

  return [
    { ...link, attrs: { ...link.attrs, ...STYLED_LINK_ATTRIBUTES } },
    ...others,
    ...underline,
    { type: 'textStyle', attrs: { ...textStyle?.attrs, color: textStyle?.attrs?.color || LINK_DEFAULT_COLOR } }
  ]
}

export const normalizeLegacyLinks = (content: JSONContent): JSONContent => {
  if (!content || typeof content !== 'object') return content

  return {
    ...content,
    ...(Array.isArray(content.marks) ? { marks: withLegacyLinkStyle(content.marks) } : {}),
    ...(Array.isArray(content.content) ? { content: content.content.map(normalizeLegacyLinks) } : {})
  }
}
